/**
 * Email-context send gate, client side.
 *
 * The backend hard-blocks `chat/trigger-ai-response` while an attached email
 * thread's file attachments are still extracting — otherwise the model would
 * see `[attachment: ...]` metadata instead of the document text
 * (ws_processors/chat.py, ChatTriggerAIResponseProcessor). It signals that
 * block with `MessagingDisabledException`, NOT the `AttachmentsStillProcessing`
 * error the file-upload path uses, so the existing retry loop in
 * WebsocketProvider never engages and the turn stalls until a page refresh.
 *
 * Rather than pattern-match that ambiguous error, the client holds the trigger
 * itself: `email_attachment` chips arrive with `processing_complete: false`,
 * which tells us up front that firing now would be rejected. The trigger is
 * stashed and re-fired once `useEmailAttachmentStatusPolling` reports every
 * thread terminal.
 *
 * Note the chip payload is a point-in-time snapshot the backend never rewrites
 * — it stays `pending` in chat history forever. Readiness must therefore come
 * from the polled `emailContext.attachmentStatusByThreadId` map, never from the
 * chip. See docs/email-attachment-fe-be.md §2.7 in backend-core-v2.
 */

export interface EmailAttachmentEntryLike {
  email_thread_id?: string | null;
}

export interface EmailAttachmentMessageLike {
  message_type?: string | null;
  processing_complete?: boolean;
  payload?: {
    attachments?: EmailAttachmentEntryLike[] | null;
    processing_complete?: boolean;
  } | null;
}

export interface ThreadAttachmentStatusLike {
  status?: string | null;
}

const TERMINAL_STATUSES = ["successful", "failed"];

export const isTerminalEmailAttachmentStatus = (status?: string | null): boolean =>
  TERMINAL_STATUSES.includes(status ?? "");

const isEmailAttachmentChip = (message: EmailAttachmentMessageLike): boolean =>
  message?.message_type === "email_attachment";

const chipIsComplete = (message: EmailAttachmentMessageLike): boolean =>
  message?.processing_complete === true || message?.payload?.processing_complete === true;

const threadIdsOf = (message: EmailAttachmentMessageLike): string[] =>
  (message?.payload?.attachments || [])
    .map((a) => a?.email_thread_id)
    .filter((id): id is string => Boolean(id));

/**
 * Thread ids from any `email_attachment` chip in this batch that the backend
 * reported as still extracting. Empty when the batch carries no email chip, or
 * carries only completed ones — in which case the trigger fires normally.
 */
export const findPendingEmailAttachmentThreadIds = (
  messages: EmailAttachmentMessageLike[] | null | undefined,
): string[] => {
  const ids = (messages || [])
    .filter((m) => isEmailAttachmentChip(m) && !chipIsComplete(m))
    .flatMap(threadIdsOf);
  return Array.from(new Set(ids));
};

/**
 * Every email thread id referenced by any `email_attachment` chip in the
 * thread, regardless of the chip's own stale `processing_complete`. Used on the
 * reload path, where the trigger is fired from history rather than from a live
 * WS batch and the chip's snapshot can't be trusted either way.
 */
export const collectEmailAttachmentThreadIds = (
  messages: EmailAttachmentMessageLike[] | null | undefined,
): string[] => Array.from(new Set((messages || []).filter(isEmailAttachmentChip).flatMap(threadIdsOf)));

/**
 * True once every given thread has a polled status of `successful` or
 * `failed`. A thread absent from the map has not been polled yet and counts as
 * not ready, so an empty map never reads as done. An empty id list is
 * vacuously ready — there is nothing to wait on.
 */
export const areEmailThreadsTerminal = (
  emailThreadIds: string[] | null | undefined,
  attachmentStatusByThreadId: Record<string, ThreadAttachmentStatusLike> | null | undefined,
): boolean =>
  (emailThreadIds || []).every((id) => isTerminalEmailAttachmentStatus(attachmentStatusByThreadId?.[id]?.status));
